import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { ToastService } from 'src/app/services/toast.service';
import { CartService } from 'src/app/services/marketplace/cart.service';
import { Cart, CartLine, CouponPreview } from 'src/app/services/marketplace/marketplace.models';

/** The smallest cart checkout accepts (the server enforces it too). */
const CHECKOUT_MINIMUM = 5;

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CartComponent implements OnInit {
  cart: Cart | null = null;
  loading = true;
  /** Why the cart couldn't load: an expired sign-in, or anything else. */
  loadError: 'signin' | 'other' | null = null;
  busyLineId: number | null = null;
  readonly minimum = CHECKOUT_MINIMUM;

  // ---- discount code ----
  couponOpen = false;
  couponInput = '';
  coupon: CouponPreview | null = null;
  couponError: string | null = null;
  applyingCoupon = false;
  readonly skeletons = [1, 2];

  constructor(
    private cartService: CartService,
    private toaster: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadCart();
  }

  loadCart(): void {
    this.loading = true;
    this.loadError = null;
    this.cartService.getCart().subscribe({
      next: (cart) => {
        this.cart = cart;
        this.loading = false;
        this.cdr.detectChanges();
        this.recheckCoupon();
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.status === 401 ? 'signin' : 'other';
        this.cdr.detectChanges();
      },
    });
  }

  get activeLines(): CartLine[] {
    return this.cart?.lines.filter((line) => !line.saved_for_later) ?? [];
  }

  get savedLines(): CartLine[] {
    return this.cart?.lines.filter((line) => line.saved_for_later) ?? [];
  }

  get subtotal(): number {
    return Number(this.cart?.subtotal) || 0;
  }

  /** What the buyer pays: the subtotal, less any discount code. */
  get total(): number {
    return this.coupon ? Number(this.coupon.total) || 0 : this.subtotal;
  }

  /** How much more is needed to reach the checkout minimum (0 when reached). */
  get shortOfMinimum(): number {
    return Math.max(0, CHECKOUT_MINIMUM - this.subtotal);
  }

  /** 0–100, for the minimum progress bar. */
  get minimumProgress(): number {
    return Math.min(100, (this.subtotal / CHECKOUT_MINIMUM) * 100);
  }

  money(value: string | number | null | undefined): string {
    return `$${(Number(value) || 0).toFixed(2)}`;
  }

  /** "$10.00", or "Free" for a free item. */
  priceLabel(value: string | number | null | undefined): string {
    return Number(value) ? this.money(value) : 'Free';
  }

  /** A digital item is owned once — more copies would only repeat the same downloads. */
  isDigital(line: CartLine): boolean {
    return line.listing_detail?.fulfillment_type === 'digital';
  }

  updateQuantity(line: CartLine, quantity: number): void {
    if (quantity < 1) {
      return;
    }
    this.busyLineId = line.id;
    this.cartService
      .updateQuantity(line.id, quantity)
      .pipe(finalize(() => { this.busyLineId = null; this.cdr.detectChanges(); }))
      .subscribe({
        next: (cart) => { this.cart = cart; this.recheckCoupon(); },
        error: () => this.toaster.showNote('Could not update quantity.', 'danger'),
      });
  }

  removeLine(line: CartLine): void {
    this.busyLineId = line.id;
    this.cartService
      .removeLine(line.id)
      .pipe(finalize(() => { this.busyLineId = null; this.cdr.detectChanges(); }))
      .subscribe({
        next: (cart) => { this.cart = cart; this.recheckCoupon(); },
        error: () => this.toaster.showNote('Could not remove item.', 'danger'),
      });
  }

  toggleSaveForLater(line: CartLine): void {
    this.busyLineId = line.id;
    this.cartService
      .toggleSaveForLater(line.id)
      .pipe(finalize(() => { this.busyLineId = null; this.cdr.detectChanges(); }))
      .subscribe({
        next: (cart) => { this.cart = cart; this.recheckCoupon(); },
        error: () => this.toaster.showNote('Could not update your cart.', 'danger'),
      });
  }

  // ---- discount code -----------------------------------------------------

  applyCoupon(): void {
    const code = this.couponInput.trim();
    if (!code || this.applyingCoupon) return;
    this.applyingCoupon = true;
    this.couponError = null;
    this.cartService.previewCoupon(code).subscribe({
      next: (preview) => {
        this.applyingCoupon = false;
        this.coupon = preview;
        this.cartService.appliedCoupon = preview.code;
        this.couponInput = '';
        this.couponOpen = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.applyingCoupon = false;
        if (err?.status !== 401) {
          this.couponError = this.couponMessage(err) || "That code couldn't be used.";
        }
        this.cdr.detectChanges();
      },
    });
  }

  removeCoupon(): void {
    this.coupon = null;
    this.couponError = null;
    this.cartService.appliedCoupon = null;
  }

  /**
   * Re-checks the applied code after the cart changed (an item from the
   * code's shop may have been removed). A code that no longer works is
   * dropped, with the reason shown.
   */
  private recheckCoupon(): void {
    const code = this.cartService.appliedCoupon;
    if (!code || !this.activeLines.length) {
      this.coupon = null;
      return;
    }
    this.cartService.previewCoupon(code).subscribe({
      next: (preview) => {
        this.coupon = preview;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (err?.status === 401) return;
        this.coupon = null;
        this.cartService.appliedCoupon = null;
        this.couponOpen = true;
        this.couponError = `${code}: ${this.couponMessage(err) || 'no longer applies.'}`;
        this.cdr.detectChanges();
      },
    });
  }

  private couponMessage(err: any): string | null {
    const body = err?.error;
    return (Array.isArray(body) ? body[0] : body?.detail) || null;
  }

  goToCheckout(): void {
    if (this.shortOfMinimum > 0 || !this.activeLines.length) return;
    this.router.navigate(['/marketplace/checkout']);
  }

  continueShopping(): void {
    this.router.navigate(['/store']);
  }
}
